import { type JSX, useEffect, useRef } from 'react';

import { cn } from '@change/ds-utils';

import type { RootShareRecommendations } from 'src/app/pages/starterDashboardV2/shared/hooks';

import { useIsScrollable } from '../../hooks/useIsScrollable';
import { useShareRecommendationsTracking } from '../../hooks/useShareRecommendationsTracking';
import type { Props as MainProps } from '../../index';
import { SocialGroupCard } from '../SocialGroupCard';

import { ChipsContainer } from './components/ChipsContainer';
import { ScrollButtons } from './components/ScrollButtons';
import { useActiveSection } from './hooks/useActiveSection';
import { useScrollControl } from './hooks/useScrollControl';

export function RecommendationsCarousel({
	shareRecommendations,
	isReskin = false,
	viewContext,
}: {
	shareRecommendations: RootShareRecommendations;
	isReskin?: boolean;
	viewContext: MainProps['viewContext'];
}): JSX.Element | null {
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const canScroll = useIsScrollable(scrollContainerRef);
	const { activeSection, handleSectionScroll, scrollToSection, clearUserIntent } = useActiveSection();
	const { canScrollLeft, canScrollRight, updateScrollState, scrollByOneCard } = useScrollControl(scrollContainerRef);
	const showChips = shareRecommendations.reddit.length > 0 && shareRecommendations.facebook.length > 0 && canScroll;
	const allRecommendations = [
		...shareRecommendations.reddit.map((rec, index) => ({ ...rec, platform: 'reddit', index }) as const),
		...shareRecommendations.facebook.map((rec, index) => ({ ...rec, platform: 'facebook', index }) as const),
	];
	const {
		actions: { handleArrowButtonClick, handleChipClick },
	} = useShareRecommendationsTracking();

	useEffect(() => {
		updateScrollState();
	}, [shareRecommendations, updateScrollState]);

	const handleScroll = () => {
		if (!scrollContainerRef.current) return;
		clearUserIntent();
		handleSectionScroll(scrollContainerRef.current);
		updateScrollState();
	};

	const handleSectionClick = (platform: 'reddit' | 'facebook') => {
		if (!scrollContainerRef.current) return;
		scrollToSection(platform, scrollContainerRef.current);
		handleChipClick(platform, 'petition_dashboard_group_rec');
	};

	const handleScrollClick = (direction: 'left' | 'right') => {
		clearUserIntent();
		scrollByOneCard(direction);
		handleArrowButtonClick(direction, 'petition_dashboard_group_rec');
	};

	const reskinStyles = isReskin ? 'px-0 md:px-0 pr-4 md:pr-12' : '';

	return (
		<>
			{canScroll ? (
				<div
					className={cn('flex w-full flex-row justify-between px-8', reskinStyles)}
					data-testid="carousel-controls"
					data-qa="carousel-controls"
				>
					<div className="flex-grow">
						<ChipsContainer activeSection={activeSection} onSectionClick={handleSectionClick} showChips={showChips} />
					</div>
					<ScrollButtons
						canScrollLeft={canScrollLeft}
						canScrollRight={canScrollRight}
						onScrollLeft={() => handleScrollClick('left')}
						onScrollRight={() => handleScrollClick('right')}
					/>
				</div>
			) : null}
			<div
				ref={scrollContainerRef}
				className="w-100 flex snap-x gap-4 overflow-x-auto"
				sx={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
				onScroll={handleScroll}
				data-testid="recommendations-container"
				data-qa="recommendations-container"
				tabIndex={-1}
			>
				{allRecommendations.map((rec, i) => {
					return (
						<SocialGroupCard
							isFirst={i === 0}
							isLast={i === allRecommendations.length - 1}
							key={`${rec.platform}-${rec.index}`}
							recommendation={rec}
							platform={rec.platform}
							isReskin={isReskin}
							viewContext={viewContext}
						/>
					);
				})}
			</div>
		</>
	);
}
